/**
 * Recursively strip sensitive keys from any object. Mutates a clone,
 * not the original — designed to slot into Sentry's `beforeSend` and
 * PostHog's `before_send`-equivalent hooks on both backend (Node) and
 * frontend (browser, React Native) SDKs without modifying the input.
 *
 * Plan §Data Classification §What's never logged:
 *   "Plain-text passwords. Bedrock prompts containing user PII. Stripe
 *    card numbers. Chat message bodies. Photo originals."
 *
 * The list is the *common* set of fields that have leaked from
 * analytics / breadcrumb / event payloads in past dating-app incidents
 * — emails (correlatable across breaches), phone E164 strings, full
 * date-of-birth, exact coordinates. App-specific extensions (e.g. a
 * service that handles ID-verification artifacts) layer their own
 * field names on top via the `extraKeys` option.
 *
 * Defaults to depth 8 — a real Sentry event payload nests ~3-4 levels.
 */

const DEFAULT_SENSITIVE_KEYS: ReadonlySet<string> = new Set([
  // Contact identifiers — correlatable across breaches.
  'email',
  'phone',
  'phonenumber',
  'phone_number',
  'phonee164',
  'phone_e164',
  // Identity / age — sensitive for under-18 safety + matrimonial PII.
  'dateofbirth',
  'date_of_birth',
  'dob',
  // Credentials — never log under any circumstances.
  'password',
  'token',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'idtoken',
  'id_token',
  'bearertoken',
  'bearer_token',
  'authtoken',
  'auth_token',
  'sessiontoken',
  'session_token',
  'apikey',
  'api_key',
  'secretkey',
  'secret_key',
  'signingsecret',
  'signing_secret',
  'webhooksecret',
  'webhook_secret',
  'authorization',
  'cookie',
  'set-cookie',
  // Liveness / verification artifacts — Rekognition Face Liveness
  // sessions correlate to face captures; ID document numbers are
  // government-issued identifiers.
  'livenesssessionid',
  'liveness_session_id',
  'iddocumentnumber',
  'id_document_number',
  'passportnumber',
  'passport_number',
  'driverslicense',
  'drivers_license',
  'nationalid',
  'national_id',
  // Financial — taxes / banking / cards. Stripe.js tokenizes cards
  // before they reach our backend, but defense-in-depth: if a card
  // number ever ends up in an error payload, redact.
  'ssn',
  'socialsecuritynumber',
  'social_security_number',
  'taxid',
  'tax_id',
  'ein',
  'creditcard',
  'credit_card',
  'cardnumber',
  'card_number',
  'cvv',
  'cvc',
  'bankaccount',
  'bank_account',
  'iban',
  'routingnumber',
  'routing_number',
]);

const GEO_KEYS: ReadonlySet<string> = new Set(['lat', 'lng', 'latitude', 'longitude']);

const REDACTED = '[redacted]';
const TRUNCATED = '[truncated]';
const DEFAULT_MAX_DEPTH = 8;

export interface ScrubPiiOptions {
  /** Additional field names to redact, lowercased internally. */
  extraKeys?: readonly string[];
  /** Maximum recursion depth before bailing out with `[truncated]`. */
  maxDepth?: number;
}

export function scrubPii(value: unknown, options: ScrubPiiOptions = {}): unknown {
  const sensitiveKeys = options.extraKeys
    ? new Set<string>([...DEFAULT_SENSITIVE_KEYS, ...options.extraKeys.map((k) => k.toLowerCase())])
    : DEFAULT_SENSITIVE_KEYS;
  const maxDepth = options.maxDepth ?? DEFAULT_MAX_DEPTH;
  return walk(value, 0, sensitiveKeys, maxDepth);
}

function walk(value: unknown, depth: number, sensitive: ReadonlySet<string>, max: number): unknown {
  if (depth > max) return TRUNCATED;
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => walk(v, depth + 1, sensitive, max));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      const lower = key.toLowerCase();
      if (sensitive.has(lower)) {
        out[key] = REDACTED;
        continue;
      }
      // Nested geo: redact home.lat / home.lng (and lng's cousins) but
      // keep other home.* fields readable so the event still has
      // enough context to debug.
      if (key === 'home' && v && typeof v === 'object') {
        const nested: Record<string, unknown> = {};
        for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
          nested[k2] = GEO_KEYS.has(k2.toLowerCase())
            ? REDACTED
            : walk(v2, depth + 2, sensitive, max);
        }
        out[key] = nested;
        continue;
      }
      out[key] = walk(v, depth + 1, sensitive, max);
    }
    return out;
  }
  return value;
}
